import {
  computed,
  inject,
  Injectable,
  isDevMode,
  signal,
  Signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { ADMIN_PAGE_ROLES } from '../constants/roles';
import { MenuItem } from '../models/menu-item.model';
import { CurrentUser, Organization } from '../models/auth/current-user.model';

@Injectable({
  providedIn: 'root',
})
export class TokenStorageService {
  private readonly TOKEN_KEY = 'auth-token';
  private readonly USER_KEY = 'auth-user';
  /** Stand-in for `pinCodeHash` until the next sign-in brings the real hash. */
  private readonly PIN_CODE_SET_MARKER = 'set';
  private currentUser = signal<CurrentUser | null>(null);
  private userToken = signal<string | null>(null);
  private kassaUrl = environment.kassaUrl;
  private router = inject(Router);
  /**
   * The authenticated principal. Asserted non-null for ergonomics: every consumer
   * reads it only inside the authenticated shell (post-`userGuard`), where it is
   * always present — mirroring how the call sites are already written. It is `null`
   * before login / after `signOut()`; guard reads in `auth`/guards still hold at runtime.
   */
  readonly currentUser$ = this.currentUser.asReadonly() as Signal<CurrentUser>;
  readonly token$ = this.userToken.asReadonly();
  readonly allModulesWithBlocked = computed(() => {
    const data = this.currentUser$()?.organization;
    const modulesRaw: MenuItem[] = (data?.allModules ?? []) as MenuItem[];
    const blockedRaw: MenuItem[] = (data?.excludeModules ?? []) as MenuItem[];
    const blockedModules = blockedRaw.map((m) => ({ ...m, blocked: true }));
    return [...modulesRaw, ...blockedModules];
  });
  private privilegeSet = new Set<string>();
  private roleSet = new Set<string>();
  isResto$ = computed(() => {
    const org = this.currentUser$()?.organization;
    if (org?.tariff?.productType === 'RESTO') return true;
    return false;
  });
  /**
   * The org is served by a reseller/partner, which bills it directly — so the
   * self-service billing screens (balance, payment history, tariff purchase) do
   * not apply. `partnerId` is `null` or `0` for direct customers.
   */
  hasPartner$ = computed(
    () => (this.currentUser$()?.organization?.partnerId ?? 0) > 0,
  );
  /** Organization owner — the exact role `ROLE_USER`; `ROLE_USER_*` sub-roles are staff. */
  readonly isOwner$ = computed(
    () => this.currentUser$()?.roles?.includes('ROLE_USER') ?? false,
  );
  readonly hasPinCode$ = computed(
    () => !!this.currentUser$()?.user?.pinCodeHash,
  );
  adminPageRoles = ADMIN_PAGE_ROLES;
  init() {
    const userStr = window.sessionStorage.getItem(this.USER_KEY);
    if (userStr) {
      const user = JSON.parse(userStr);
      this.currentUser.set(user);
      this.redirectToKassaIfNeeded();
      this.rebuildPrivilegeSet(user);
      this.rebuildRoleSet(user);
    }
    const token = window.sessionStorage.getItem(this.TOKEN_KEY);
    if (token) this.userToken.set(token);
  }
  private rebuildPrivilegeSet(user: CurrentUser | null) {
    this.privilegeSet.clear();
    if (user?.privileges && Array.isArray(user.privileges)) {
      for (const p of user.privileges) {
        this.privilegeSet.add(p);
      }
    }
  }
  private rebuildRoleSet(user: CurrentUser | null) {
    this.roleSet.clear();
    if (user?.roles && Array.isArray(user.roles)) {
      for (const role of user.roles) {
        this.roleSet.add(role);
      }
    }
  }
  signOut() {
    localStorage.removeItem('userInfo');
    localStorage.removeItem('positionTableCols');
    localStorage.removeItem('new_reduction_tbl');
    window.sessionStorage.clear();
    this.userToken.set(null);
    // The shell reads currentUser$ as non-null until it is destroyed: drop the
    // in-memory user only after the router has left it. A skipped same-url
    // navigation (signOut on /auth) resolves too, with false.
    this.router.navigateByUrl('/auth').then(() => {
      this.currentUser.set(null);
      this.privilegeSet.clear();
    });
  }
  saveToken(token: string) {
    window.sessionStorage.setItem(this.TOKEN_KEY, token);
    this.userToken.set(token);
  }
  saveUser(user: CurrentUser) {
    window.sessionStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this.currentUser.set(user);
    this.rebuildPrivilegeSet(user);
    this.rebuildRoleSet(user);
  }
  /**
   * After `api/auth/pincode` succeeds. The endpoint returns no body, so a set PIN
   * is stored as a marker — `hasPinCode$` only reads its presence.
   */
  setPinCodeState(isSet: boolean) {
    const current = this.currentUser();
    if (!current) return;
    this.saveUser({
      ...current,
      user: {
        ...current.user,
        pinCodeHash: isSet ? this.PIN_CODE_SET_MARKER : null,
      },
    });
  }
  hasOnePrivilege(priv?: string): boolean {
    if (this.roleSet.has('ROLE_USER')) return true;
    if (priv) return this.privilegeSet.has(priv);
    return false;
  }
  /** Reads the currentUser$ signal — reactive when called inside a computed/effect */
  hasModule(moduleName: string): boolean {
    const allModules = this.currentUser$()?.organization?.allModules as
      | MenuItem[]
      | undefined;
    const matches = (modules?: MenuItem[]): boolean =>
      modules?.some((m) => m.name === moduleName || matches(m.subModules)) ??
      false;
    return matches(allModules);
  }
  updateOrganization(
    newOrgData: Organization,
    updateAllModules: boolean = false,
  ) {
    const currentData = this.currentUser();
    if (!currentData || !currentData.organization) return;
    const updatedData = {
      ...currentData,
      organization: {
        ...newOrgData,
        allModules: updateAllModules
          ? newOrgData.allModules
          : currentData.organization.allModules,
        defaultModules: updateAllModules
          ? newOrgData.defaultModules
          : currentData.organization.defaultModules,
        excludeModules: updateAllModules
          ? newOrgData.excludeModules
          : currentData.organization.excludeModules,
        plugins: newOrgData.plugins ?? currentData.organization.plugins,
      },
    };
    this.currentUser.set(updatedData);
    window.sessionStorage.setItem(this.USER_KEY, JSON.stringify(updatedData));
  }
  /** A cashier or an account without roles — not an admin-panel account. */
  isCashierAccount(): boolean {
    const role = this.currentUser()?.roles?.[0];
    return !role || role === 'ROLE_CASHIER';
  }
  /**
   * Accounts that work in the cashier app (cashiers, admin-panel staff roles,
   * dealer branches, KASSA organizations) leave for `kassaUrl`; in dev mode they
   * stay here. Returns true when the page is being replaced.
   */
  redirectToKassaIfNeeded(): boolean {
    if (isDevMode() || !this.isKassaAccount()) return false;
    window.location.replace(this.kassaUrl);
    return true;
  }
  private isKassaAccount(): boolean {
    if (this.isCashierAccount()) return true;
    const user = this.currentUser();
    const role = user?.roles?.[0] ?? '';
    return (
      this.adminPageRoles.includes(role) ||
      role === 'ROLE_DEALER_FILIAL' ||
      user?.organization?.type === 'KASSA'
    );
  }
}
